<template>
  <v-dialog
    v-model="dialogVisible"
    width="700px"
    @click:outside="closeDialog"
  >
    <v-card>
      <v-card-title>
        Bulk Product Search
      </v-card-title>

      <v-card-text>
        <div class="d-flex flex-column gap-4">
          <v-textarea
            v-model="barcodes"
            label="Paste UPCs (comma or newline separated)"
            outlined
            rows="6"
          />
          <v-btn
            color="primary"
            :loading="loading"
            @click="submitBulkSearch"
          >
            Search
          </v-btn>

          <div v-if="errorMsg" class="warning--text">{{ errorMsg }}</div>

          <div v-if="searchResults.length">
          <div class="d-flex flex-row my-2">
          <h4>Search Results</h4>
          <v-spacer></v-spacer>
          <v-btn
            small
            icon
            :loading="downloadLoading"
            @click="download"
          ><v-icon>{{ icons.mdiDownload }}</v-icon></v-btn>
          
          </div>
          <v-row dense>
            <v-col
              v-for="product in searchResults"
              :key="product.barcode"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <v-card outlined class="pa-3 d-flex flex-column justify-space-between" style="height: 250px;">
                <!-- Product Image -->
                <SecureImage
                  v-if="product.found && product.images && product.images.length"
                  :src="product.images[0].thumbnail"
                  contain
                  height="120"
                  class="mb-2"
                />
                <div
                  v-else
                  class="avatar-fallback mb-2"
                >
                  <v-icon size="48" color="grey lighten-1">
                    {{ icons.mdiCameraOff }}
                  </v-icon>
                </div>


                <!-- Product Name -->
                <div class="text-center mb-1">
                  <span class="font-weight-medium text-truncate d-block">{{ product.name || 'Unknown Product' }}</span>
                  <small class="text-muted">{{ product.barcode }}</small>
                </div>

                <!-- Missing / Not Found Highlight -->
                <div v-if="!product.found" class="text-center warning--text mt-auto">
                  Not Found
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="secondary" @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch } from '@vue/composition-api'
import SecureImage from '../../SecureImage.vue';
import useProduct from '../useProduct'
import { mdiCameraOff, mdiDownload } from '@mdi/js'

export default {
  components:{
    SecureImage
  },
  props: {
    isDialogOpen: { type: Boolean, required: true }
  },
  setup(props, { emit }) {
    const barcodes = ref('')
    const searchResults = ref([])
    const errorMsg = ref('')
    const loading = ref(false)
    const products = ref([])

    const dialogVisible = ref(props.isDialogOpen)

    watch(dialogVisible, val => {
      emit('update:is-dialog-open', val)
    })
    watch(() => props.isDialogOpen, val => {
      dialogVisible.value = val
    })

    const closeDialog = () => {
      dialogVisible.value = false
      init()
    }

    const { 
      fetchBulkProducts,
      downloadImages,
      downloadLoading,

     } = useProduct()

    const submitBulkSearch = async () => {
      try {
        loading.value = true

        const rawList = barcodes.value.split(/[\n,]+/).map(b => b.trim())

        const invalidEntries = rawList.filter(b => !/^\d+$/.test(b))
        const cleanedList = rawList.filter(b => /^\d+$/.test(b))

        if (!cleanedList.length) {
          if (!invalidEntries.length) {
            errorMsg.value = 'Please enter at least one valid UPC/EAN.'
          } else {
            errorMsg.value = `No valid UPCs found. Invalid entries: ${invalidEntries.join(', ')}`
          }
          searchResults.value = []
          return
        }

        products.value = await fetchBulkProducts(cleanedList)

        // Check if products is an array
        if (!Array.isArray(products.value)) {
          errorMsg.value = 'Unexpected response from server'
          searchResults.value = []
          return
        }

        // Map all entered UPCs - Partial and Full UPC codes - Mark missing products
        const resultMap = cleanedList.map(upc => {
          const matchedProducts = products.value.filter(p => p.barcode.includes(upc))
          return matchedProducts.length
            ? matchedProducts.map(p => ({ ...p, found: true }))
            : [{ barcode: upc, name: null, found: false }]
        }).flat()

        searchResults.value = resultMap
        errorMsg.value = ''

      } catch (err) {
        errorMsg.value = err.response?.data?.message || 'Error fetching products'
      } finally {
        loading.value = false
      }
    }

    const download = async () => {
      const payload = {
        upcs: products.value.map(upc => upc.barcode)
      }
      const response = await downloadImages(payload)
    }

    const init = () => {
      barcodes.value = ''
      searchResults.value = []
      errorMsg.value = ''
      loading.value = false
    }

    return {
      barcodes,
      searchResults,
      errorMsg,
      loading,
      dialogVisible,
      downloadLoading,
      closeDialog,
      submitBulkSearch,
      download,
      icons: {
        mdiCameraOff,
        mdiDownload
      },
    }
  }
}
</script>


<style scoped>
.warning--text {
  color: red;
}
.bg-error-lighten-4 {
  background-color: #ffe5e5;
}

.avatar-fallback {
  width: 120px;
  height: 120px;
  border-radius: 10%;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}


</style>